import { useMemo } from 'react';
import { ACTIVE_THEME_NAME } from '../../../branding';
import { logPageView } from '../../../gql/hooks/mutations/logPageView';
import { RESOURCE_FILE_QUERY } from '../../../gql/queries/RESOURCE_FILE_QUERY';
import { useTranslations } from '../../../hooks/translations';
import { apolloService, authService } from '../../../services';
import { CourseUtils, CurriculumUtils } from '../../../utils';
import moment from 'moment';

const localize = ( locs, locale ) => typeof locs == 'string' ? locs : ( locs.find( n => n.locale == locale ) ?? locs.find( n => n.locale == 'en-US' ) ?? locs[ 0 ] ).content;

function flatten( item ) {
    if ( item == undefined || item.constructor === Array ) {return item;}
    if ( item.edges ) {return item.edges;}
    if ( item.node ) {return item.node;}
    return item;
}

function sanitizeBasic( item, locale ) {
    let ret = flatten( item );
    ret.name = localize( ret.name, locale );
    ret.description = localize( ret.description, locale );
    return ret;
}

// Unified place to translate BE status name into FE friendly status name
function getStatus( status, objectType ) {
    if ( !status ) {
        return 'NOT_STARTED';
    }

    return [ 'NOT_STARTED', 'IN_PROGRESS', 'COMPLETED' ].includes( status ) ? status :
        // Status is ASSIGNED, which has different meanings in different objects smh
        objectType == 'learningObject' ? 'NOT_STARTED' :
        objectType == 'course' ? 'IN_PROGRESS' : null;
}
// if ( !status ) {
//     return 'NOT_STARTED';
// }

// if ( [ 'NOT_STARTED', 'IN_PROGRESS', 'COMPLETED' ].includes( status ) ) {
//     return status;
// }
    
// if ( objectType === 'learningObject' ) {
//     return 'NOT_STARTED';
// } 
    
// if ( objectType === 'course' ) {
//     return 'IN_PROGRESS';
// }
const getLearningObjectProgress = ( learningObject ) => {
    const status = getStatus( learningObject.assignment?.status, 'learningObject' );
    if ( status === 'IN_PROGRESS' ) {
        return 0.5;
    } else {
        if ( status === 'COMPLETED' ) {
            return 1;
        } else {
            return 0;
        }
    }
};

const sanitizeLearningObject = ( course, locale = 'en-US', learningObject ) => {  
    const item = learningObject || course?.learningObjects?.[ 0 ];
    if ( item ) {    
        const assignment = item.assignment ?? course.enrollment?.assignments?.find( a => a.learningObjectId == item.learningObjectId );
        const completionDate = new Date( assignment?.completedAt * 1000 );
        
        return {
            _type: 'lesson',
            id: item.id ?? item.learningObjectId, 
            courseId: course.id,
            key: item.id ?? item.learningObjectId,
            expireAt: item.expireAt,
            name: localize( course.name, locale ),
            objectName: localize( item.name, locale ),
            progress: getLearningObjectProgress( item ),
            status: getStatus( assignment?.status, 'learningObject' ),
            mimeType: item.content?.mimetype ?? item.assets?.[ 0 ]?.asset?.mimetype ?? '',
            format: item.format,
            url: item.content?.url ?? item.assets?.[ 0 ]?.asset?.url ?? '',
            completedAt: `${ completionDate.toLocaleString( 'default', { month: 'long' } ) } ${ completionDate.getDate() }, ${ completionDate.getFullYear() }`,
            completedAtMs: assignment?.completedAt * 1000, 
            score: assignment?.score,
            assignment,
            assets: item.assets,
            duration: item.duration,
            lxpFileId: item.content?.lxpFileId ?? item.assets?.[ 0 ]?.asset?.lxpFileId ?? '',
            originalData: item
        }; 
    } else {
        return [];
    }
};

const sanitizeLearningObjects = ( course, locale = 'en-US' ) => {  
    return course.learningObjects?.map( i => sanitizeLearningObject( course, locale, i ) ).filter( i => !Array.isArray( i ) ) ?? [];
};

const sanitizeCourses = ( courses, locale, { syncParents, syncResources } ) => {
    return flatten( courses )
        .map( c => c.node ?? c )
        .map( ( course ) => {

            const learningObject = sanitizeLearningObject( course );
                
            return {
                type: 'course', 
                id: course.id ?? course.courseId, 
                key: course.id ?? course.courseId,
                tags: course.lxp?.tags,
                parentIds: syncParents ? course.curriculumIds ?? [] : [],
                thumbnail: course.thumbnailImage?.url || '',
                heroImage: course.heroImage?.url || '',
                name: localize( course.name, locale ),
                description: localize( course.description, locale ),
                progress: CourseUtils.getProgressPercentage( course ),
                ratingAverage: course.ratingAverage || 0,
                fetchCertificateUrl: course.fetchCertificateUrl,
                status: getStatus( course.enrollment?.status, 'course' ),
                certificate: ( course.courseCertificates || course.enrollment?.userCertificate?.courseCertificate ) && {
                    earned: !!course.enrollment?.userCertificate?.userCertificateUrl,
                    link: course.enrollment?.userCertificate?.userCertificateUrl,
                    hasCertificate: !!course.courseCertificates && course.courseCertificates.some( c => !c.brand || c.brand.name.toLowerCase() === ACTIVE_THEME_NAME.toLowerCase() )
                },
                resources: syncResources ? sanitizeResources( course.resources, locale ) : undefined,
                learningObject: learningObject,
                learningObjects: sanitizeLearningObjects( course ),
                prerequisiteData: course.prerequisiteData ?? undefined,
                updatedAt: course.updatedAt,
                createdAt: course.createdAt,
                review: ( course.enrollment?.rating || course.enrollment?.review ) ? { rating: course.enrollment.rating, review: course.enrollment.review } : null,
                originalData: course,
                completedAt: getFormattedDate( new Date( learningObject.completedAtMs ), locale ),
                completedAtMs: learningObject.completedAtMs,
                totalDuration: learningObject.duration,
                totalScore: learningObject.score,
                lxp: course.lxp,
            };
    } );
};

const getFormattedDate = ( date, locale ) => {
    if ( date ) {
        if ( locale === 'en-US' || locale === 'en-CA' ) {
    return `${ date.toLocaleString( 'default', { month: 'long' } ) } ${ date.getDate() }, ${ date.getFullYear() }`;
        } else {
            return `${ date.getDate() } ${ date.toLocaleString( 'default', { month: 'long' } ) }, ${ date.getFullYear() }`;
        }
    } else {
        return '';
    }
};

const sanitizeTranscript = ( courses, locale ) => {

    return courses
        .map( c => ( {
            id: c.courseId,
            key: c.courseId,
            name: ( c.name.find( n => n.locale == locale ) ?? c.name.find( n => n.locale == 'en-US' ) ).content,
            type: 'course',
            progressPercentage: c.percentComplete / 100.0,
            certificate: !!c.userCertificate?.userCertificateUrl,
            duration: moment.duration( c.duration, 'minutes' ),
            score: c.assignments[ 0 ].score,
            completion: moment( c.assignments[ 0 ].completedAt * 1000 ),
            viewPath: `/course/${ c.courseId }`
        } ) );
};

const sanitizeCurricula = ( curriculums, locale, { syncParents, syncResources } ) => {
    return curriculums
                .map( c => c.node ?? c )
                .map( ( curriculum ) => ( {
                    originalData: curriculums,
                    id: curriculum.id,
                    tags: curriculum.tags,
                    tagIds: curriculum.tagIds,
                    learningProfileIds: curriculum.learningProfileIds,
                    name: localize( curriculum.name, locale ),
                    description: localize( curriculum.description, locale ),
                    thumbnail: curriculum.thumbnailImage?.url || '',
                    heroImage: curriculum.heroImage?.url || '',
                    type: 'curriculum',
                    categories: syncParents ? curriculum.lxp?.categories.map( c => sanitizeBasic( c, locale ) ) : [],
                    courses: curriculum.courses ? sanitizeCourses( curriculum.courses, locale, { syncParents, syncResources } ) : undefined,
                    progress: CurriculumUtils.getProgressPercentage( curriculum ),
                    lxp: curriculum.lxp ?? {
                        relatedResourceBrandIds: curriculum.node.brandIds,
                        relatedResourceLocaleIds: curriculum.node.localeIds,
                        relatedResourceCompanyRelationshipIds: curriculum.node.companyRelationshipIds,
                        relatedResourceProductIds: curriculum.node.productIds,
                    },
            } ) );
};

const sanitizeResources = ( data, locale ) => {

    const download = async ( resource, callback ) => {

        apolloService.query( {
            query: RESOURCE_FILE_QUERY,
            variables: {
                resourceId: resource.id,
                resourceFileId: resource.fileId
            },
            context: { headers: { authorization: `Bearer ${ authService.getAccessToken() }` } },
            fetchPolicy: 'network-only'
        } ).then( file => {
            const url = flatten( file.data.resourceFile ).url;
            logPageView( { pageType: 'resource-download', pageUrl: url } );
            window.open( url, '_blank' );
            callback( file );
        } );
    };
    
    return data.map( r => r.node ?? r )
        .map( r => ( { 
            ...r,
            localization: flatten( flatten( r.resourceLocalizations )?.find( rl => rl.localeCode == locale )
                            ?? flatten( r.resourceLocalizations )?.find( rl => rl.isDefault )
                            ?? flatten( r.resourceLocalizations )?.find( rl => rl.localeCode == 'en-US' )
                            ?? flatten( r.resourceLocalizations )?.[ 0 ]
                            ?? null )
        } ) )
        .filter( r => r.localization && r.isPublished )
        .map( r => ( {
            originalData: r,
            type: 'resource',
            id: r.id ?? r.resourceId,
            tags: r.tags,
            tagIds: r.tagIds,
            learningProfileIds: r.learningProfileIds,
            description: r.localization.description,
            name: r.localization.name,
            fileId: flatten( r.localization.resourceFile ).id || flatten( r.localization.resourceFile ).resourceFileId,
            filename: flatten( r.localization.resourceFile )?.genericFile?.filename ?? flatten( r.localization.resourceFile )?.lxpFile?.filename,
            mimetype: flatten( r.localization.resourceFile )?.genericFile?.mimetype ?? flatten( r.localization.resourceFile )?.lxpFile?.mimetype,
            download,
            lxp: r.lxp ?? {
                relatedResourceBrandIds: r.brandIds,
                relatedResourceLocaleIds: r.localeIds,
                relatedResourceCompanyRelationshipIds: r.node?.companyRelationshipIds,
                relatedResourceProductIds: r.productIds,
            },
        } ) );
};

const sanitizeNotifications = ( data, locale ) => {

    return data.map( r => r.node ?? r )
        .filter( r => r.isPublished )
        .map( r => ( { 
            originalData: r,
            type: 'notification',
            id: r.notificationId,
            isSticky: r.isSticky,
            isFeatured: r.isFeatured || r.isSticky,
            publishedAt: r.publishedAt,
            weight: r.isSticky ? r.weight : undefined,
            name: ( r.name.find( l => l.locale == locale ) ?? r.name.find( l => l.locale == 'en-US' ) ?? r.name[ 0 ] ).content,
            description: ( r.description.find( l => l.locale == locale ) ?? r.description.find( l => l.locale == 'en-US' ) ?? r.description[ 0 ] ).content,
        } ) );
};


export const useSanitizer = () => {
    const { locale } = useTranslations();

    return useMemo( () => ( {
        sanitizeResources: ( data, loc = locale ) => sanitizeResources( data, loc ),
        sanitizeCourses: ( data, opts, loc = locale,  ) => sanitizeCourses( data, loc, opts ),
        sanitizeTranscript: ( data, loc = locale ) => sanitizeTranscript( data, loc ),
        sanitizeCurricula: ( data, opts, loc = locale ) => sanitizeCurricula( data, loc, opts ),
        sanitizeNotifications: ( data, loc = locale ) => sanitizeNotifications( data, loc ),
        sanitizeBasic: ( data, loc = locale ) => sanitizeBasic( data, loc ),
    } ), [ locale ] );
};